import { ArrowLeft, Copy, CreditCard, Home, Maximize2, PackageSearch, RotateCcw, X } from "lucide-react";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, LinkButton } from "../components/Button";
import { EmptyState, SectionHeader } from "../components/UI";
import { formatCurrency, formatDate, getMyOrders, getOrderDetail, getPaymentMethods, getUserId, repayOrder, resolveAssetUrl, type Order, type PaymentMethodsConfig } from "../lib/api";
import { useAppState } from "../state/AppState";

const STLViewer = lazy(() => import("../components/STLViewer").then((module) => ({ default: module.STLViewer })));

const ORDER_PROGRESS_STEPS = ["Confirmed", "In Queue", "Printing", "Printed", "Shipped"];
const PENDING_PAYMENT_KEY = "pd_pending_payment";

function getOrderDisplayId(order: Pick<Order, "display_id" | "order_number" | "id">) {
  return order.display_id || (typeof order.order_number === "string" ? order.order_number : "") || `PDO${String(order.id).padStart(4, "0")}`;
}

function orderProgressPercent(order: Order) {
  if (typeof order.progress_percent === "number") return Math.max(0, Math.min(100, order.progress_percent));
  const fallback: Record<string, number> = { received: 0, queued: 25, printing: 50, completed: 75, shipped: 100 };
  return fallback[(order.status || "").toLowerCase()] ?? 0;
}

function getCurrentStepIndex(order: Order) {
  const pct = orderProgressPercent(order);
  const currentStatus = (order.progress_label || order.status || "").toLowerCase();
  const statusMap: Record<string, number> = { confirmed: 0, "in queue": 1, printing: 2, printed: 3, shipped: 4 };
  for (const [key, value] of Object.entries(statusMap)) {
    if (currentStatus.includes(key)) return value;
  }
  if (pct >= 100) return 4;
  if (pct >= 75) return 3;
  if (pct >= 50) return 2;
  if (pct >= 25) return 1;
  return 0;
}

function isPaymentFailed(order: Order) {
  return String(order.payment_status || "").toLowerCase() === "failed";
}

function canRepayOrder(order: Order) {
  return ["failed", "payment_pending"].includes(String(order.payment_status || "").toLowerCase());
}

function OrderProgress({ order, compact = false }: { order: Order; compact?: boolean }) {
  const currentIndex = getCurrentStepIndex(order);
  const fillPct = (currentIndex / (ORDER_PROGRESS_STEPS.length - 1)) * 100;
  const paymentFailed = isPaymentFailed(order);

  return (
    <div className="hide-scrollbar" style={{ overflowX: "auto", whiteSpace: "nowrap", paddingBottom: compact ? 0 : 12, marginBottom: compact ? 0 : 24 }}>
      <div className={`progress-tracker-container ${compact ? "progress-compact" : "progress-hero"}${paymentFailed ? " progress-payment-failed" : ""}`} style={{ minWidth: 600 }}>
        <div className="progress-track">
          <div className="progress-track-bg" />
          <div className="progress-track-fill" style={{ width: `${fillPct}%` }} />
        </div>
        <div className="progress-nodes">
          {ORDER_PROGRESS_STEPS.map((status, index) => {
            const isActive = index <= currentIndex;
            const isCurrent = index === currentIndex;
            return (
              <div
                key={status}
                className={`progress-node-item${isActive ? " active" : ""}${isCurrent ? " current" : ""}`}
                data-index={index}
              >
                <div className={`progress-node-circle${paymentFailed && index === 0 ? " payment-failed" : ""}`}>
                  {paymentFailed && index === 0 ? (
                    <X className="progress-node-cross" size={compact ? 12 : 15} aria-hidden="true" />
                  ) : !compact && isActive ? (
                    <svg className="progress-node-check" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : null}
                </div>
                <div className="progress-node-label">{status}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function getItemQuantity(item: Record<string, unknown>) {
  const value = Number(item.quantity ?? item.qty ?? 1);
  return Number.isFinite(value) && value > 0 ? value : 1;
}

function getItemPrice(item: Record<string, unknown>) {
  const unitPrice = Number(item.price ?? item.total ?? 0);
  return Number.isFinite(unitPrice) ? unitPrice : 0;
}

function getItemName(item: Record<string, unknown>, index: number) {
  return String(item.name || item.title || item.product_name || item.label || `Item ${index + 1}`);
}

function getItemFilename(item: Record<string, unknown>, index: number) {
  return String(
    item.filename ||
      item.stl_filename ||
      item.model_filename ||
      item.file_name ||
      item.name ||
      `file-${index + 1}.stl`
  );
}

function getItemStlUrl(item: Record<string, unknown>) {
  const stl = item.stl_file || item.stlFile || item.model_file_url || item.file_url;
  return typeof stl === "string" && stl ? resolveAssetUrl(stl) : "";
}

function getItemColorHex(item: Record<string, unknown>) {
  const direct = item.stlColorHex || item.color_hex || item.hex;
  return typeof direct === "string" ? direct : "";
}

function extractItemSettings(item: Record<string, unknown>, order: Order) {
  const quoteOptions = typeof item.quote_options === "object" && item.quote_options ? item.quote_options as Record<string, unknown> : {};
  const detail = String(item.detail || "");
  const detailParts = detail.includes("·") ? detail.split("·").map((part) => part.trim()) : [];
  const orderMeta = typeof order.order_metadata === "object" && order.order_metadata ? order.order_metadata : {};

  return {
    material: String(quoteOptions.material || detailParts.find((part) => !part.startsWith("Color:")) || orderMeta.material || "Standard"),
    color: String(quoteOptions.color || detailParts.find((part) => part.startsWith("Color:"))?.replace("Color:", "").trim() || orderMeta.color || "Default"),
    infill: String(quoteOptions.infill || orderMeta.infill || "Standard"),
    support: String(quoteOptions.support || orderMeta.support || "Yes"),
    nozzle: String(quoteOptions.nozzle || orderMeta.nozzle || ""),
    supportStyle: String(quoteOptions.support_style || orderMeta.support_style || ""),
    infillPattern: String(quoteOptions.infill_pattern || orderMeta.infill_pattern || ""),
    topBottomLayers: String(quoteOptions.top_bottom_layers || orderMeta.top_bottom_layers || ""),
    extraRequests: String(quoteOptions.extra_requests || item.extra_requests || order.extra_requests || "")
  };
}

function getPrimaryStlUrl(order: Order, cartItems: Array<Record<string, unknown>>) {
  for (const item of cartItems) {
    const stl = getItemStlUrl(item);
    if (stl) return stl;
  }
  return "";
}

function normalizeSupportLabel(value: string) {
  const lower = value.toLowerCase();
  if (["yes", "true", "1", "enabled"].includes(lower)) return "Yes";
  if (["no", "false", "0", "disabled"].includes(lower)) return "No";
  return value || "Standard";
}

function getShippingCost(order: Order, shipping: Record<string, unknown>) {
  const value = shipping.charged_amount ?? shipping.amount ?? order.delivery_cost ?? 0;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
}

function formatNozzle(value: string) {
  if (!value) return "";
  return value.includes("mm") ? value.replace("mm", " mm") : `${value} mm`;
}

export function OrdersPage() {
  const { user } = useAppState();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const userId = getUserId(user);
    if (!userId) {
      setOrders([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    getMyOrders(userId, user?.token)
      .then((data) => {
        if (!cancelled) setOrders(data);
      })
      .catch(() => {
        if (!cancelled) {
          setOrders([]);
          setError("Orders could not be loaded.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!user) {
    return (
      <EmptyState icon={<PackageSearch size={24} />} title="Login To View Orders">
        Your order history appears here after checkout.
      </EmptyState>
    );
  }

  return (
    <section>
      <SectionHeader title="My Orders" className="orders-header" />
      {loading ? <p className="muted">Loading orders…</p> : null}
      {error ? <p className="muted">{error}</p> : null}
      {!loading && !orders.length ? (
        <EmptyState icon={<PackageSearch size={24} />} action={<LinkButton to="/quote">Get A Quote</LinkButton>}>
          No orders yet.
        </EmptyState>
      ) : null}
      <div className="orders-stack">
        {orders.map((order) => {
          const displayId = getOrderDisplayId(order);
          return (
            <Link className="order-card order-row-card order-card-link" key={order.id} to={`/orders/${encodeURIComponent(displayId)}`}>
              <div className="order-card-header">
                <div className="order-card-id">Order #{displayId}</div>
                <div className="order-card-total">{formatCurrency(order.total_amount)}</div>
                <div className="order-card-date">{formatDate(order.created_at)}</div>
              </div>
              <OrderProgress order={order} compact />
            </Link>
          );
        })}
      </div>
    </section>
  );
}

export function OrderDetailPage() {
  const { addToast, user } = useAppState();
  const { orderId } = useParams();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [viewerResetKey, setViewerResetKey] = useState(0);
  const previewRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const userId = getUserId(user);
    if (!userId || !orderId) {
      setOrder(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    getOrderDetail(orderId, userId, user?.token)
      .then((data) => {
        if (!cancelled) {
          setOrder(data);
          setSelectedIndex(0);
        }
      })
      .catch((fetchError) => {
        if (!cancelled) {
          setOrder(null);
          setError(fetchError instanceof Error ? fetchError.message : "Order could not be loaded.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orderId, user]);

  if (!user) {
    return (
      <EmptyState icon={<PackageSearch size={24} />} title="Login To View Orders" action={<LinkButton to="/orders">Back To Orders</LinkButton>}>
        Your order details appear here after checkout.
      </EmptyState>
    );
  }

  if (loading && !order) return <p className="muted">Loading order…</p>;

  if (error && !order) {
    return (
      <EmptyState icon={<PackageSearch size={24} />} title="Order Not Available" action={<LinkButton to="/orders">Back To Orders</LinkButton>}>
        {error}
      </EmptyState>
    );
  }

  if (!order) return null;

  const displayId = getOrderDisplayId(order);
  const cartItems = Array.isArray(order.cart_items) && order.cart_items.length
    ? order.cart_items
    : [{
        name: "Custom Upload",
        quantity: 1,
        price: Number(order.base_cost || 0),
        detail: String(order.order_metadata?.material || "")
      }];
  const selectedItem = cartItems[Math.min(selectedIndex, cartItems.length - 1)] || cartItems[0];
  const selectedSettings = extractItemSettings(selectedItem, order);
  const selectedStlUrl = getItemStlUrl(selectedItem) || getPrimaryStlUrl(order, cartItems);
  const selectedFilename = getItemFilename(selectedItem, selectedIndex);
  const selectedName = getItemName(selectedItem, selectedIndex);
  const selectedQuantity = getItemQuantity(selectedItem);
  const selectedColorHex = getItemColorHex(selectedItem);
  const shipping = order.shipping || (typeof order.order_metadata?.shipping === "object" ? order.order_metadata.shipping as Record<string, unknown> : {});
  const shippingCost = getShippingCost(order, shipping);
  const shippingMethod = String(shipping.label || shipping.method || "Standard");
  const addressLines = order.delivery_address
    ? [
        order.delivery_address.name,
        order.delivery_address.line1,
        order.delivery_address.line2,
        [order.delivery_address.city, order.delivery_address.state, order.delivery_address.pincode].filter(Boolean).join(", "),
        order.delivery_address.country
      ].filter(Boolean).map(String)
    : [];
  const copyAddress = async () => {
    if (!addressLines.length) return;
    try {
      await navigator.clipboard.writeText(addressLines.join("\n"));
      addToast("Delivery address copied", "success");
    } catch {
      addToast("Could not copy address", "error");
    }
  };
  const openFullscreen = () => {
    previewRef.current?.requestFullscreen?.();
  };
  const canRepay = canRepayOrder(order);

  return (
    <section className="order-detail-page-container">
      <div className="order-detail-shell order-detail-compact order-detail-redesign">
        <div className="order-detail-top">
          <div>
            <h1>Order #{displayId}</h1>
            <div className="text-sm text-muted">{formatDate(order.created_at)}</div>
          </div>
          <div className="order-detail-actions">
            <LinkButton to="/orders" variant="ghost" icon={<ArrowLeft size={16} />}>Back to orders</LinkButton>
          </div>
        </div>

        <OrderProgress order={order} />

        {isPaymentFailed(order) ? (
          <div className="order-payment-alert">
            <div className="order-payment-alert-copy">
              <strong>Payment failed</strong>
              <span>This order is saved, but payment was not completed. You can retry payment from this order.</span>
            </div>
            {canRepay ? (
              <LinkButton
                to={`/orders/${encodeURIComponent(displayId)}/pay`}
                variant="secondary"
                className="order-payment-repay-btn"
                icon={<CreditCard size={16} />}
              >
                Repay
              </LinkButton>
            ) : null}
          </div>
        ) : null}

        <div className="order-detail-master-grid">
          <div className="order-detail-left">
            <div className="order-card-panel order-summary-card">
              <h2>Order summary</h2>
              <div className="order-summary-row"><span>Base cost</span><strong>{formatCurrency(order.base_cost)}</strong></div>
              <div className="order-summary-row"><span>Shipping method</span><strong>{shippingMethod}</strong></div>
              <div className="order-summary-row"><span>Shipping cost</span><strong>{formatCurrency(shippingCost)}</strong></div>
              <div className="order-separator" />
              <div className="order-summary-row order-summary-total"><span>Total</span><strong>{formatCurrency(order.total_amount)}</strong></div>
            </div>

            <div className="order-card-panel">
              <div className="order-card-title-row">
                <h2>Delivery address</h2>
                {addressLines.length ? (
                  <Button variant="ghost" size="sm" icon={<Copy size={15} />} onClick={copyAddress}>Copy</Button>
                ) : null}
              </div>
              {addressLines.length ? (
                <div className="order-address-text">
                  {addressLines.map((line, index) => (
                    <div key={`${line}-${index}`} className={index === 0 ? "order-address-name" : undefined}>{line}</div>
                  ))}
                </div>
              ) : (
                <div className="text-muted text-sm">No delivery address provided.</div>
              )}
            </div>

            <div className="order-card-panel">
              <div className="order-card-title-row">
                <h2>Ordered files</h2>
                <span className="order-badge">{cartItems.length}</span>
              </div>
              <div className="order-file-list hide-scrollbar" role="listbox" aria-label="Ordered files">
                {cartItems.map((item, index) => {
                  const quantity = getItemQuantity(item);
                  const unitPrice = getItemPrice(item);
                  const settings = extractItemSettings(item, order);
                  const isSelected = index === selectedIndex;
                  const filename = getItemFilename(item, index);
                  return (
                    <button
                      key={`${getItemName(item, index)}-${index}`}
                      className={`order-file-item${isSelected ? " selected" : ""}`}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      title={filename}
                      onClick={() => {
                        setSelectedIndex(index);
                        setViewerResetKey((value) => value + 1);
                      }}
                    >
                      <span className="order-file-main">
                        <strong className="order-file-title">{getItemName(item, index)}</strong>
                        <span className="order-file-filename">{filename}</span>
                        <span className="order-file-description">
                          {[settings.material, settings.color, settings.infill].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                      <span className="order-file-actions">
                        <span>Qty {quantity}</span>
                        <strong>{formatCurrency(unitPrice * quantity)}</strong>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="order-detail-panel">
            <div className="order-card-panel order-selected-card">
              <div className="order-card-title-row">
                <div>
                  <h2>Selected file</h2>
                  <strong className="order-selected-name">{selectedName}</strong>
                  <div className="order-selected-file" title={selectedFilename}>{selectedFilename}</div>
                </div>
                <div className="order-selected-badges">
                  <span className="order-badge">{selectedSettings.material}</span>
                  <span className="order-badge">Qty {selectedQuantity}</span>
                </div>
              </div>
              <div className="order-preview-toolbar">
                <Button variant="ghost" size="sm" icon={<RotateCcw size={15} />} onClick={() => setViewerResetKey((value) => value + 1)}>Reset</Button>
                <Button variant="ghost" size="sm" icon={<Maximize2 size={15} />} onClick={openFullscreen}>Fullscreen</Button>
              </div>
              <div className="order-detail-model-viewer" ref={previewRef}>
                {selectedStlUrl ? (
                  <Suspense fallback={<div className="order-preview-skeleton">Loading STL...</div>}>
                    <STLViewer
                      key={`${selectedStlUrl}-${viewerResetKey}`}
                      url={selectedStlUrl}
                      color={selectedSettings.color}
                      colorHex={selectedColorHex || undefined}
                      mode="product"
                      interactive={true}
                      autoRotate={false}
                    />
                  </Suspense>
                ) : (
                  <div className="cart-model-loading">No 3D model available.</div>
                )}
              </div>
            </div>

            <div className="order-card-panel">
              <h2>Print settings</h2>
              <div className="order-settings-subtitle">For {selectedFilename}</div>
              <dl className="order-settings-list">
                <div><dt>Material</dt><dd>{selectedSettings.material}</dd></div>
                <div>
                  <dt>Colour</dt>
                  <dd>
                    <span className="order-color-dot" style={{ backgroundColor: selectedColorHex || selectedSettings.color }} />
                    {selectedSettings.color}
                  </dd>
                </div>
                <div><dt>Infill</dt><dd>{selectedSettings.infill}</dd></div>
                <div><dt>Supports</dt><dd><span className="order-support-badge">{normalizeSupportLabel(selectedSettings.support)}</span></dd></div>
                {selectedSettings.supportStyle ? <div><dt>Support style</dt><dd>{selectedSettings.supportStyle}</dd></div> : null}
                {selectedSettings.nozzle ? <div><dt>Nozzle</dt><dd>{formatNozzle(selectedSettings.nozzle)}</dd></div> : null}
                {selectedSettings.infillPattern ? <div><dt>Infill pattern</dt><dd>{selectedSettings.infillPattern}</dd></div> : null}
                {selectedSettings.topBottomLayers ? <div><dt>Top / bottom layers</dt><dd>{selectedSettings.topBottomLayers}</dd></div> : null}
              </dl>
              {selectedSettings.extraRequests ? (
                <div style={{ marginTop: "var(--space-sm)", paddingTop: "var(--space-sm)", borderTop: "1px dashed var(--border-color)", whiteSpace: "pre-wrap" }}>
                  <div className="text-sm text-muted" style={{ marginBottom: 4 }}>Item Note</div>
                  <div className="text-sm">{selectedSettings.extraRequests}</div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {error ? <div className="text-sm text-muted">{error}</div> : null}
      </div>
    </section>
  );
}

export function RepayOrderPage() {
  const { addToast, user } = useAppState();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [paymentConfig, setPaymentConfig] = useState<PaymentMethodsConfig>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState<"manual" | "phonepe" | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const userId = getUserId(user);
    if (!userId || !orderId) {
      setOrder(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    Promise.all([
      getOrderDetail(orderId, userId, user?.token),
      getPaymentMethods().catch(() => ({} as PaymentMethodsConfig)),
    ])
      .then(([orderData, paymentData]) => {
        if (cancelled) return;
        setOrder(orderData);
        setPaymentConfig(paymentData);
      })
      .catch((fetchError) => {
        if (!cancelled) {
          setOrder(null);
          setError(fetchError instanceof Error ? fetchError.message : "Order could not be loaded.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orderId, user]);

  async function submitRepayment(provider: "manual" | "phonepe") {
    if (!order || !user?.token) return;
    setSubmitting(provider);
    setError("");
    try {
      const response = await repayOrder(order.id, provider, user.token);
      if (provider === "manual") {
        addToast("Manual payment selected", "success");
        navigate(`/orders/${encodeURIComponent(getOrderDisplayId(order))}`);
        return;
      }

      const payUrl = "extra" in response ? response.extra?.pay_page_url : "";
      const gatewayOrderId = "gateway_order_id" in response ? response.gateway_order_id : "";
      if (!payUrl || !gatewayOrderId) {
        throw new Error("PhonePe is not ready for this order.");
      }
      localStorage.setItem(PENDING_PAYMENT_KEY, JSON.stringify({
        order_id: order.id,
        display_id: getOrderDisplayId(order),
        gateway_order_id: gatewayOrderId,
        provider: response.provider,
      }));
      window.location.assign(String(payUrl));
    } catch (repayError) {
      setError(repayError instanceof Error ? repayError.message : "Payment could not be restarted.");
      setSubmitting(null);
    }
  }

  if (!user) {
    return (
      <EmptyState icon={<PackageSearch size={24} />} title="Login To Repay" action={<LinkButton to="/orders">Back To Orders</LinkButton>}>
        Login to continue payment for this order.
      </EmptyState>
    );
  }

  if (loading) return <p className="muted">Loading payment options...</p>;

  if (error && !order) {
    return (
      <EmptyState icon={<PackageSearch size={24} />} title="Payment Not Available" action={<LinkButton to="/orders">Back To Orders</LinkButton>}>
        {error}
      </EmptyState>
    );
  }

  if (!order) return null;

  const displayId = getOrderDisplayId(order);
  const canRepay = canRepayOrder(order);
  const phonepeReady = Boolean(paymentConfig.phonepe_enabled && paymentConfig.phonepe_ready);

  return (
    <section className="repay-page">
      <SectionHeader title={`Complete payment for ${displayId}`} className="orders-header" />
      <div className="repay-card">
        <div className="repay-summary">
          <span>Amount due</span>
          <strong>{formatCurrency(order.total_amount)}</strong>
          <small>{isPaymentFailed(order) ? "Previous payment failed. Choose a payment method below." : "This order is awaiting payment."}</small>
        </div>

        {!canRepay ? (
          <div className="order-payment-alert">
            <strong>Payment is not required</strong>
            <span>This order is not in a repayable state.</span>
          </div>
        ) : null}

        <div className="repay-options">
          <button type="button" className="repay-option" disabled={!canRepay || Boolean(submitting)} onClick={() => submitRepayment("manual")}>
            <span className="repay-option-icon"><Home size={18} /></span>
            <span><strong>Manual payment</strong><small>Confirm order and settle offline.</small></span>
          </button>
          <button type="button" className="repay-option" disabled={!canRepay || !phonepeReady || Boolean(submitting)} onClick={() => submitRepayment("phonepe")}>
            <span className="repay-option-icon"><CreditCard size={18} /></span>
            <span><strong>PhonePe</strong><small>{phonepeReady ? "Pay securely using PhonePe." : "PhonePe is not available right now."}</small></span>
          </button>
        </div>

        {error ? <div className="text-sm text-muted">{error}</div> : null}
        <LinkButton to={`/orders/${encodeURIComponent(displayId)}`} variant="ghost" icon={<ArrowLeft size={16} />}>Back to order</LinkButton>
      </div>
    </section>
  );
}
